<template>
  <b-row class="ForApproval mb-5">
    <b-col cols="12" lg="10" offset-lg="1">
      <Notification v-bind.sync="notification" />

      <b-card>
        <b-card-title
          title-tag="h2"
          class="text-center"
          v-text="cardTitle"
        />

        <Notification v-bind="pendingNotifOptions" class="mb-5 rounded-0" />

        <b-nav tabs class="mb-1">
          <template v-for="(tab, idx) in tabList">
            <b-nav-item
              :key="`tab_${idx}`"
              :to="tab.link"
              :active="tab.active"
              :disabled="tab.disabled"
            >
              {{ tab.label }}
            </b-nav-item>
          </template>
        </b-nav>

        <!-- TODO: filter and search -->
        <Datatable
          :resources="resources"
          :options="datatableOptions"
          @update:perPage="updatePerPage"
          @updateSort="updateSort"
          @updatePage="updatePage"
        >
          <template v-slot:cell(actions)="scope">
            <span
              v-if="!$get(scope, `item.${requestViewFieldCheck}`, false)"
              class="text-danger"
            >
              <v-icon name="exclamation-circle" />
              Error in record
            </span>
            <b-link
              v-else
              :to="{
                name: requestDetailsRouteName,
                params: {
                  [requestViewFieldParam]: $get(scope, `item.${requestViewField}`, ''),
                },
              }"
            >
              View
            </b-link>
          </template>
        </Datatable>
      </b-card>
    </b-col>
  </b-row>
</template>
<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import Notification from '@/components/Notification';
import Datatable from '@/components/Datatable';
import ResourceService from '@/services/ResourceService';

const resource = new ResourceService();

export default {
  name: 'ForApproval',
  components: {
    Notification,
    Datatable,
  },
  created() {
    this.getRequests();
  },
  data() {
    return {
      requestList: [],
      perPage: 10,
      page: 1,
      sortBy: 'submitDate',
      sortDesc: false,
      total: 0,
    };
  },
  watch: {
    $route: 'getRequests',
  },
  computed: {
    requestType() {
      return this.$get(this.$route, 'meta.type', '');
    },
    requestViewField() {
      switch (this.requestType) {
        case 'timesheet':
          return 'code';
        default:
          return 'key';
      }
    },
    requestViewFieldParam() {
      switch (this.requestType) {
        default:
          return 'key';
      }
    },
    requestViewFieldCheck() {
      switch (this.requestType) {
        // dependent on request type
        default:
          return this.requestViewField;
      }
    },
    requestDetailsRouteName() {
      if (this.requestType === 'timesheet') {
        return 'approval';
      }
      return `${this.requestType}-approval`;
    },
    cardTitle() {
      return `PENDING ${this.$upper(this.requestType)} REQUESTS`;
    },
    pendingNotifOptions() {
      const tsCount = this.total;
      const forApprovalMessage = `${tsCount || 'No'} request${tsCount === 1 ? '' : 's'} for your approval`;
      const loadingMessage = `Loading pending ${this.requestType}...`;
      const icon = {
        name: this.isLoadingRequest ? 'circle-notch' : 'clock',
        spin: this.isLoadingRequest,
      };

      return {
        icon,
        show: true,
        message: this.isLoadingRequest ? loadingMessage : forApprovalMessage,
      };
    },
    tabList() {
      return [
        {
          link: { name: 'approval.timesheet' },
          label: 'Timesheet',
          active: this.navIsActive('approval.timesheet'),
        },
        {
          link: { name: 'approval.overtime' },
          label: 'Overtime',
          active: this.navIsActive('approval.overtime'),
        },
        {
          link: { name: 'approval.ob' },
          label: 'Official Business',
          active: this.navIsActive('approval.ob'),
        },
      ];
    },
    datatableOptions() {
      return {
        small: true,
        pagination: true,
        dtControl: true,
        fields: this.tableFields || [],
        loading: this.isLoadingRequest,
        trClass: this.rowClass,
        page: this.page,
        perPage: this.perPage,
        total: this.total,
      };
    },
    tableFields() {
      // TODO: fields dependent on request type
      const numField = {
        key: 'id',
        label: '#',
        class: 'text-center',
      };
      const nameField = {
        key: 'fullName',
        label: 'Full Name',
        sortable: true,
      };
      if (this.requestType === 'timesheet') {
        return [
          numField,
          nameField,
          {
            key: 'cutoffPeriod',
            label: 'Cutoff Period',
            sortable: true,
            class: 'text-center',
          },
          {
            key: 'submitDate',
            label: 'Submit Date',
            sortable: true,
            formatter: val => this.$fd(val, 'MMM DD, YYYY'),
            class: 'text-center',
          },
          {
            key: 'actions',
            label: 'Actions',
            sortable: false,
            class: 'text-center',
          },
        ];
      }
      return [
        numField,
        nameField,
        {
          sortable: true,
          class: 'text-center',
          key: 'record_date',
          label: 'Request Date',
          formatter: val => (val ? this.$fd(val, 'MMM DD YYYY') : '-'),
        },
        {
          key: 'submitDate',
          label: 'Submit Date',
          sortable: true,
          formatter: val => (val ? this.$fd(val, 'MMM DD YYYY - hh:mm A') : '-'),
          class: 'text-center',
        },
        {
          key: 'status',
          name: 'Status',
          sortable: true,
          tdClass: (val, key, item) => {
            let classes = 'text-center font-weight-bold';

            const statusID = this.$get(item, 'status_id', null);
            switch (statusID) {
              case this.approved:
                classes += ' bg-success text-white';
                break;
              case this.rejected:
                classes += ' bg-danger text-white';
                break;
              case this.cancelled:
                classes += ' bg-secondary';
                break;
              default:
                break;
            }

            return classes;
          },
          formatter: this.$startCase,
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
          class: 'text-center',
        },
      ];
    },
    resources() {
      // TODO: dependent on request type
      return (this.requestList || []).map((ts) => {
        const otherData = {};
        const code = this.$get(ts, 'code', '');
        if (!code) {
          otherData.noCode = true;
        }
        return Object.assign({}, ts, otherData);
      });
    },
    ...mapGetters([
      'isApprover',
      'userData',
      'isLoadingRequest',
    ]),
    ...mapState(['notification']),
  },
  methods: {
    updateSort(payload) {
      this.sortBy = this.$get(payload, 'sortBy', null);
      this.sortDesc = this.$get(payload, 'sortDesc', false);
      this.getRequests();
    },
    updatePerPage(val) {
      this.perPage = val;
      this.page = 1;
      this.getRequests();
    },
    updatePage(val) {
      this.page = val;
      this.getRequests();
    },
    getRequests() {
      const { id } = this.userData;
      const params = {
        page: this.page,
        perPage: this.perPage,
        sortBy: this.sortBy,
        sortDesc: this.sortDesc,
      };

      this.startLoadingRequest();
      this.startLoadingPage();
      // fetch all requests for approval of user
      resource.get(`/${this.requestType}/for-approval/${id}`, params)
        .then(({
          success,
          data,
          total,
          message,
        }) => {
          if (!success) {
            return this.setNotification({
              show: true,
              dismissible: true,
              variant: 'danger',
              message: message || 'An error occurred',
              icon: { name: 'exclamation-triangle' },
            });
          }
          this.requestList = data;
          this.total = total;

          return success;
        })
        .catch(() => {
          this.setNotification({
            show: true,
            dismissible: true,
            variant: 'danger',
            message: 'An error occurred',
            icon: { name: 'exclamation-triangle' },
          });
        })
        .then(() => {
          this.stopLoadingRequest();
          this.stopLoadingPage();
        });
    },
    ...mapActions([
      'startLoadingRequest',
      'stopLoadingRequest',
      'startLoadingPage',
      'stopLoadingPage',
      'setNotification',
    ]),
  },
};
</script>
